import { createClient } from "@supabase/supabase-js";

// Get environment variables with fallbacks
const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ||
  (typeof window !== "undefined" ? window.ENV?.SUPABASE_URL : "");

const supabaseServiceKey =
  import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY ||
  (typeof window !== "undefined" ? window.ENV?.SUPABASE_SERVICE_ROLE_KEY : "");

// Use a singleton pattern for the admin client
let adminClientInstance = null;

// Initialize the Supabase admin client with service role key
const createAdminClient = () => {
  if (!adminClientInstance && supabaseUrl && supabaseServiceKey) {
    try {
      // Create a custom storage implementation that does nothing
      // This prevents the GoTrueClient from using any storage and avoids conflicts
      const noOpStorage = {
        getItem: () => null,
        setItem: () => {},
        removeItem: () => {},
        clear: () => {},
        length: 0,
        key: () => null,
      };

      // Generate a truly unique storage key using a timestamp and random component
      // This ensures the admin client is completely isolated from the main client
      const uniqueStorageKey = `supabase-admin-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;

      adminClientInstance = createClient(supabaseUrl, supabaseServiceKey, {
        auth: {
          autoRefreshToken: false,
          persistSession: false,
          storageKey: uniqueStorageKey, // Use truly unique storage key to prevent conflicts
          storage: typeof window !== "undefined" ? noOpStorage : undefined, // Use no-op storage in browser
          detectSessionInUrl: false, // Don't detect sessions
        },
        global: {
          headers: {
            "x-client-info": `ArtifactChat/AdminClient`,
          },
        },
      });

      // Validate that the admin client was created successfully
      if (!adminClientInstance.auth?.admin) {
        console.error("Admin client created but admin API not available");
        adminClientInstance = null;
      }
    } catch (error) {
      console.error("Failed to create Supabase admin client:", error);
      adminClientInstance = null;
    }
  } else if (!supabaseUrl || !supabaseServiceKey) {
    console.warn("Missing Supabase URL or service role key for admin client");
  }
  return adminClientInstance;
};

// Lazy-load the admin client to avoid immediate initialization
// This prevents the "Multiple GoTrueClient instances" warning on page load
// The Proxy ensures the client is only created when actually accessed
export const supabaseAdmin = new Proxy(
  {},
  {
    get(target, prop) {
      if (!adminClientInstance) {
        adminClientInstance = createAdminClient();
      }
      if (!adminClientInstance) return undefined;
      const value = adminClientInstance[prop];
      // Bind methods to preserve `this` context
      if (typeof value === "function") {
        return value.bind(adminClientInstance);
      }
      return value;
    },
  }
);

// Validate admin client and its capabilities
export const validateAdminClient = (client) => {
  if (!client) {
    return { isValid: false, error: "Admin client not available" };
  }

  if (!client.auth) {
    return { isValid: false, error: "Admin client auth not available" };
  }

  if (!client.auth.admin) {
    return {
      isValid: false,
      error: "Admin API not available - service role key required",
    };
  }

  return { isValid: true, error: null };
};

// Get admin client with validation
export const getAdminClient = () => {
  // If we don't have an admin client yet, try to create one
  if (!adminClientInstance) {
    createAdminClient();
  }

  // Validate the admin client
  const validation = validateAdminClient(adminClientInstance);
  if (!validation.isValid) {
    console.warn(`Admin client validation failed: ${validation.error}`);
    return null;
  }

  return adminClientInstance;
};

// Check if admin operations are available
export const isAdminAvailable = () => {
  const client = getAdminClient();
  return client !== null;
};

// Get admin configuration status for debugging
export const getAdminStatus = () => {
  const hasUrl = !!supabaseUrl;
  const hasServiceKey = !!supabaseServiceKey;
  const client = adminClientInstance;
  const hasClient = !!client;
  const hasAdminAPI = !!client?.auth?.admin;

  return {
    hasUrl,
    hasServiceKey,
    hasClient,
    hasAdminAPI,
    isFullyConfigured: hasUrl && hasServiceKey && hasClient && hasAdminAPI,
    url: hasUrl ? supabaseUrl.substring(0, 20) + "..." : "Not configured",
    serviceKeyConfigured: hasServiceKey ? "Configured" : "Not configured",
  };
};

// This client uses the service role key which bypasses Row Level Security
// Only use this for server-side operations that need admin privileges
// Never expose this key to the client-side code

export default supabaseAdmin;
